import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/layout/ProtectedRoute";
import PublicLayout from "./components/layout/PublicLayout";
import CompanyLayout from "./components/layout/CompanyLayout";
import AdminLayout from "./components/layout/AdminLayout";
import Landing from "./pages/public/Landing";
import Directory from "./pages/public/Directory";
import CompanyProfile from "./pages/public/CompanyProfile";
import CompanyReviews from "./pages/public/CompanyReviews";
import QrFeedback from "./pages/public/QrFeedback";
import TrackCase from "./pages/public/TrackCase";
import HowItWorks from "./pages/public/HowItWorks";
import Contact from "./pages/public/Contact";
import { Privacy, Terms } from "./pages/public/Legal";
import { ForgotPassword, ResetPassword } from "./pages/public/PasswordPages";
import Login from "./pages/public/Login";
import Register from "./pages/public/Register";
import CompanySignup from "./pages/public/CompanySignup";
import { VerifyEmail, Invitation } from "./pages/public/AccountLinks";
import UserLayout from "./components/layout/UserLayout";
import UserDashboard from "./pages/user/UserDashboard";
import UserGiveFeedback from "./pages/user/UserGiveFeedback";
import { UserFeedbacks, UserFeedbackDetail } from "./pages/user/UserFeedbacks";
import UserNotifications from "./pages/user/UserNotifications";
import UserProfile from "./pages/user/UserProfile";
import UserPrivacy from "./pages/user/UserPrivacy";
import AdminContact from "./pages/admin/AdminContact";
import AdminDeletions from "./pages/admin/AdminDeletions";
import AdminAbuse from "./pages/admin/AdminAbuse";
import AdminLocations from "./pages/admin/AdminLocations";
import AdminRetention from "./pages/admin/AdminRetention";
import AdminSectors from "./pages/admin/AdminSectors";
import AdminAI from "./pages/admin/AdminAI";
import AdminEmails from "./pages/admin/AdminEmails";
import Insights from "./pages/company/Insights";
import SecurityPage from "./pages/support/SecurityPage";
import Reports from "./pages/company/Reports";
import Communication from "./pages/company/Communication";
import AdminCommunication from "./pages/admin/AdminCommunication";
import Unsubscribe from "./pages/public/Unsubscribe";
import Dashboard from "./pages/company/Dashboard";
import Feedbacks from "./pages/company/Feedbacks";
import FeedbackDetail from "./pages/company/FeedbackDetail";
import Agencies from "./pages/company/Agencies";
import Services from "./pages/company/Services";
import QrCodes from "./pages/company/QrCodes";
import Team from "./pages/company/Team";
import CompanyAccount from "./pages/company/CompanyAccount";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCompanies from "./pages/admin/AdminCompanies";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminFeedbacks from "./pages/admin/AdminFeedbacks";
import AdminLogs from "./pages/admin/AdminLogs";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminInternal from "./pages/admin/AdminInternal";
import AdminBilling from "./pages/admin/AdminBilling";
import AdminTreasury from "./pages/admin/AdminTreasury";
import AdminSupport, { AdminSupportTicket } from "./pages/admin/AdminSupport";
import { CompanySupport, UserSupport, SupportTicket } from "./pages/support/SupportPages";
import Billing from "./pages/company/Billing";
import { loadReference } from "./lib/reference";
import { OfflineBar } from "./components/pwa/PwaParts";

loadReference();

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <OfflineBar />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Landing />} />
            <Route path="/comment-ca-marche" element={<HowItWorks />} />
            <Route path="/entreprises" element={<Directory />} />
            <Route path="/e/:slug" element={<CompanyProfile />} />
            <Route path="/e/:slug/avis" element={<CompanyReviews />} />
            <Route path="/f/:code" element={<QrFeedback />} />
            <Route path="/suivi" element={<TrackCase />} />
            <Route path="/desinscription" element={<Unsubscribe />} />
            <Route path="/connexion" element={<Login />} />
            <Route path="/inscription" element={<Register />} />
            <Route path="/inscription-entreprise" element={<CompanySignup />} />
            <Route path="/verifier-email" element={<VerifyEmail />} />
            <Route path="/invitation" element={<Invitation />} />
            <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
            <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
            <Route path="/confidentialite" element={<Privacy />} />
            <Route path="/conditions" element={<Terms />} />
            <Route path="/contact" element={<Contact />} />
          </Route>
          <Route path="/mon-espace" element={<ProtectedRoute><UserLayout /></ProtectedRoute>}>
            <Route index element={<UserDashboard />} />
            <Route path="deposer" element={<UserGiveFeedback />} />
            <Route path="deposer/:slug" element={<UserGiveFeedback />} />
            <Route path="feedbacks" element={<UserFeedbacks />} />
            <Route path="feedbacks/:id" element={<UserFeedbackDetail />} />
            <Route path="notifications" element={<UserNotifications />} />
            <Route path="profil" element={<UserProfile />} />
            <Route path="confidentialite" element={<UserPrivacy />} />
            <Route path="support" element={<UserSupport />} />
            <Route path="support/:id" element={<SupportTicket base="/mon-espace/support" />} />
          </Route>
          <Route path="/app" element={<ProtectedRoute need="company"><CompanyLayout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="feedbacks" element={<Feedbacks />} />
            <Route path="conversations" element={<Feedbacks />} />
            <Route path="feedbacks/:id" element={<FeedbackDetail />} />
            <Route path="rapports" element={<Reports />} />
            <Route path="analyse-ia" element={<Insights />} />
            <Route path="communication" element={<Communication />} />
            <Route path="agences" element={<Agencies />} />
            <Route path="services" element={<Services />} />
            <Route path="qr-codes" element={<QrCodes />} />
            <Route path="equipe" element={<Team />} />
            <Route path="parametres" element={<CompanyAccount />} />
            <Route path="securite" element={<SecurityPage />} />
            <Route path="abonnement" element={<Billing />} />
            <Route path="factures" element={<Billing />} />
            <Route path="support" element={<CompanySupport />} />
            <Route path="support/:id" element={<SupportTicket base="/app/support" />} />
          </Route>
          <Route path="/admin" element={<ProtectedRoute need="platform"><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="entreprises" element={<AdminCompanies />} />
            <Route path="utilisateurs" element={<AdminUsers />} />
            <Route path="feedbacks" element={<AdminFeedbacks />} />
            <Route path="journal" element={<AdminLogs />} />
            <Route path="contact" element={<AdminContact />} />
            <Route path="suppressions" element={<AdminDeletions />} />
            <Route path="signalements" element={<AdminAbuse />} />
            <Route path="annuaire" element={<AdminLocations />} />
            <Route path="conservation" element={<AdminRetention />} />
            <Route path="secteurs" element={<AdminSectors />} />
            <Route path="ia" element={<AdminAI />} />
            <Route path="emails" element={<AdminEmails />} />
            <Route path="rapports" element={<AdminDashboard />} />
            <Route path="securite" element={<SecurityPage />} />
            <Route path="communication" element={<AdminCommunication />} />
            <Route path="parametres" element={<AdminSettings />} />
            <Route path="administration" element={<AdminInternal />} />
            <Route path="facturation" element={<AdminBilling />} />
            <Route path="tresorerie" element={<AdminTreasury />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="support/:id" element={<AdminSupportTicket />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
